import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useMemo } from "react";
import { RainContributor } from "./useRainWebSocket";
import { FinalStats } from "./RainEndPhase";
import { Trophy, Award, Star, Medal } from "lucide-react";

interface TopContributor {
  twitch_id: string;
  twitch_name: string;
  totalContributed: number;
}

interface AwardFeedProps {
  contributors: RainContributor[];
  finalStats: FinalStats;
  topContributors?: TopContributor[];
}

const rankIcons = [Trophy, Medal, Award, Star, Star];

export function AwardFeed({
  contributors,
  finalStats,
  topContributors,
}: AwardFeedProps) {
  const [visible, setVisible] = useState<TopContributor[]>([]);

  const sorted = useMemo(() => {
    if (topContributors?.length) {
      return topContributors.filter(
        (c) => c?.twitch_id && c?.twitch_name
      );
    }
    return contributors
      .filter((c) => c?.twitch_id && c?.twitch_name)
      .slice(0, 4)
      .map((c) => ({
        twitch_id: c.twitch_id,
        twitch_name: c.twitch_name,
        totalContributed: c.amount,
      }));
  }, [topContributors, contributors]);

  useEffect(() => {
    setVisible([]);
    if (!sorted || sorted.length === 0) {
      return;
    }

    let i = 0;
    const id = setInterval(() => {
      if (i >= sorted.length) {
        clearInterval(id);
        return;
      }
      const item = sorted[i++];
      if (item?.twitch_id && item?.twitch_name) {
        setVisible((prev) => [...prev, item].slice(-8));
      }
    }, 250);

    return () => clearInterval(id);
  }, [sorted]);

  if (!visible.length) {
    return null;
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-4"
      >
        <p className="text-xl text-amber-300 font-black mb-1">LEADERBOARD</p>
        <p className="text-sm text-gray-300">Top Contributors</p>
      </motion.div>

      <div className="space-y-3 max-h-96 overflow-hidden w-full max-w-lg px-2">
        <AnimatePresence>
          {visible.map((contributor, index) => {
            if (!contributor?.twitch_name) return null;

            const RankIcon = rankIcons[index] || Star;
            const bgGradient =
              index === 0
                ? "from-amber-900/60 to-amber-900/30 border-amber-500/50"
                : index === 1
                  ? "from-slate-600/50 to-slate-700/30 border-slate-400/50"
                  : index === 2
                    ? "from-orange-900/50 to-orange-900/30 border-orange-500/40"
                    : "from-purple-900/40 to-purple-900/20 border-purple-500/40";

            return (
              <motion.div
                key={`${contributor.twitch_id}`}
                layout={false}
                initial={{ opacity: 0, x: -40, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -40, scale: 0.9 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 300 }}
                className={`bg-gradient-to-r ${bgGradient} border rounded-lg p-4 flex items-center justify-between gap-4 group hover:scale-105 transition-transform`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <RankIcon className="w-6 h-6 text-yellow-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">
                      {contributor.twitch_name}
                    </p>
                    <p className="text-xs text-gray-300">#{index + 1} Contributor</p>
                  </div>
                </div>

                <motion.div
                  className="flex-shrink-0 text-right"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    repeatDelay: 1,
                  }}
                >
                  <p className="text-lg font-black text-cyan-300">
                    +{contributor.totalContributed.toLocaleString()}
                  </p>
                </motion.div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
